import { looksLikeTechnicalAdminError } from './adminApiErrorPolicy';
import { isAxiosNetworkUnreachableError } from './axiosErrorMessage';

/** Stable machine codes for reviewer-safe copy — never render raw exception text. */
export type ReviewerSafeErrorCode =
  | 'SERVICE_UNAVAILABLE'
  | 'RATE_LIMITED'
  | 'NOT_FOUND'
  | 'FORBIDDEN'
  | 'VALIDATION_FAILED'
  | 'INTERNAL'
  | 'UNKNOWN';

export type ReviewerSafeError = {
  code: ReviewerSafeErrorCode;
  title: string;
  message: string;
  correlationId?: string;
  retryable: boolean;
  retryAfterMs?: number;
};

const COPY: Record<
  ReviewerSafeErrorCode,
  { title: string; message: string; retryable: boolean }
> = {
  SERVICE_UNAVAILABLE: {
    title: 'Temporarily unavailable',
    message: 'This service is briefly unavailable. Try again shortly.',
    retryable: true,
  },
  RATE_LIMITED: {
    title: 'Too many requests',
    message: 'Please wait a moment and try again.',
    retryable: true,
  },
  NOT_FOUND: {
    title: 'Not found',
    message: 'We couldn’t find what you were looking for.',
    retryable: false,
  },
  FORBIDDEN: {
    title: 'Not available',
    message: 'You don’t have access to this.',
    retryable: false,
  },
  VALIDATION_FAILED: {
    title: 'Check your input',
    message: 'Some details need fixing. Please review and try again.',
    retryable: false,
  },
  INTERNAL: {
    title: 'Something went wrong',
    message: 'An unexpected error occurred. Please try again.',
    retryable: true,
  },
  UNKNOWN: {
    title: 'Something went wrong',
    message: 'An unexpected error occurred. Please try again.',
    retryable: true,
  },
};

/** Patterns that must never reach reviewer-facing UI. */
export const REVIEWER_LEAK_PATTERNS: RegExp[] = [
  /\bat\s+.+\(.+:\d+:\d+\)/i,
  /column\s+"?[\w.]+"?\s+(does not exist|of relation)/i,
  /relation\s+"?[\w.]+"?\s+does not exist/i,
  /(SQLSTATE|SQLException|ORA-\d+|ER_\w+)/i,
  /(\/[\w.-]+){2,}\.(js|ts|py|rb|go|java):/i,
  /(insufficient_quota|out of credits|billing|rate.?limit exceeded)/i,
  /\b(openai|anthropic|stripe|twilio|sendgrid|aws|gcp|azure)\b.*\b(key|account|quota|credit)/i,
  /downstream\s+http\s+\d{3}/i,
  /\bhttp\s+\d{3}\b/i,
  /\b\d{3}\s+(internal server error|service unavailable|bad gateway|gateway timeout)\b/i,
  /\b(err_network|econnrefused|econnreset|enotfound|etimedout)\b/i,
  /\bnetwork error\b/i,
  /\b(vite|proxy|cors|localhost|127\.0\.0\.1|:\d{4,5})\b/i,
  /\bemployer-verification\b/i,
  /\borchestrator\b/i,
  /cannot reach .+ on the proxy/i,
  /npm run dev/i,
  /\.env\b/i,
  /cannot read propert(y|ies) of/i,
  /undefined is not an object/i,
  /maximum call stack/i,
];

export function looksLikeSensitiveReviewerMessage(raw?: string | null): boolean {
  const normalized = typeof raw === 'string' ? raw.trim() : '';
  if (!normalized) return false;
  if (looksLikeTechnicalAdminError(normalized)) return true;
  return REVIEWER_LEAK_PATTERNS.some((pattern) => pattern.test(normalized));
}

function statusToCode(status?: number): ReviewerSafeErrorCode | undefined {
  if (status == null || !Number.isFinite(status)) return undefined;
  const map: Record<number, ReviewerSafeErrorCode> = {
    400: 'VALIDATION_FAILED',
    401: 'FORBIDDEN',
    403: 'FORBIDDEN',
    404: 'NOT_FOUND',
    409: 'VALIDATION_FAILED',
    422: 'VALIDATION_FAILED',
    402: 'SERVICE_UNAVAILABLE',
    429: 'RATE_LIMITED',
    503: 'SERVICE_UNAVAILABLE',
  };
  return map[status] ?? (status >= 500 ? 'INTERNAL' : 'UNKNOWN');
}

type AxiosLike = {
  message?: string;
  code?: string;
  response?: {
    status?: number;
    headers?: Record<string, string | undefined> & { get?: (key: string) => string | undefined };
    data?: unknown;
  };
};

function readCorrelationIdFromBody(data: unknown): string | undefined {
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    const err = o.error;
    if (err && typeof err === 'object') {
      const cid = (err as Record<string, unknown>).correlationId;
      if (typeof cid === 'string' && cid.trim()) return cid.trim();
    }
    if (typeof o.correlationId === 'string' && o.correlationId.trim()) return o.correlationId.trim();
    if (typeof o.requestId === 'string' && o.requestId.trim()) return o.requestId.trim();
  }
  return undefined;
}

function readCorrelationIdFromAxios(ax: AxiosLike): string | undefined {
  const fromBody = readCorrelationIdFromBody(ax.response?.data);
  if (fromBody) return fromBody;
  const headers = ax.response?.headers;
  if (headers) {
    const fromGet =
      typeof headers.get === 'function'
        ? headers.get('x-correlation-id') ?? headers.get('x-request-id')
        : undefined;
    const raw =
      fromGet ??
      headers['x-correlation-id'] ??
      headers['X-Correlation-Id'] ??
      headers['x-request-id'] ??
      headers['X-Request-Id'];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
  }
  return undefined;
}

function readEnvelopeCode(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const o = data as Record<string, unknown>;
  const err = o.error;
  if (err && typeof err === 'object') {
    const code = (err as Record<string, unknown>).code;
    if (typeof code === 'string' && code.trim()) return code.trim();
  }
  return undefined;
}

function readEnvelopeMessage(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const o = data as Record<string, unknown>;
  const err = o.error;
  if (err && typeof err === 'object') {
    const msg = (err as Record<string, unknown>).message;
    if (typeof msg === 'string' && msg.trim()) return msg.trim();
  }
  if (typeof o.message === 'string' && o.message.trim()) return o.message.trim();
  if (typeof o.error === 'string' && o.error.trim()) return o.error.trim();
  return undefined;
}

function mapEnvelopeCode(code: string): ReviewerSafeErrorCode {
  const upper = code.toUpperCase();
  if (upper in COPY) return upper as ReviewerSafeErrorCode;
  if (upper.includes('RATE')) return 'RATE_LIMITED';
  if (upper.includes('UNAVAILABLE') || upper.includes('TIMEOUT')) return 'SERVICE_UNAVAILABLE';
  if (upper.includes('NOT_FOUND')) return 'NOT_FOUND';
  if (upper.includes('FORBIDDEN') || upper.includes('UNAUTHOR')) return 'FORBIDDEN';
  if (upper.includes('VALID')) return 'VALIDATION_FAILED';
  return 'UNKNOWN';
}

/** Classify any failure into reviewer-safe copy. Never surfaces thrown `message`. */
export function toReviewerSafeError(input: {
  err?: unknown;
  status?: number;
  body?: unknown;
  fallbackCode?: ReviewerSafeErrorCode;
}): ReviewerSafeError {
  const ax = (input.err ?? {}) as AxiosLike;
  const status = input.status ?? ax.response?.status;
  const body = input.body ?? ax.response?.data;

  let code: ReviewerSafeErrorCode =
    (readEnvelopeCode(body) ? mapEnvelopeCode(readEnvelopeCode(body)!) : undefined) ??
    statusToCode(status) ??
    input.fallbackCode ??
    'UNKNOWN';

  if (input.err != null && isAxiosNetworkUnreachableError(input.err)) {
    code = 'SERVICE_UNAVAILABLE';
  }

  const base = COPY[code] ?? COPY.UNKNOWN;
  const serverMessage = readEnvelopeMessage(body);
  const message =
    serverMessage && !looksLikeSensitiveReviewerMessage(serverMessage)
      ? serverMessage
      : base.message;

  const correlationId = readCorrelationIdFromBody(body) ?? readCorrelationIdFromAxios(ax);

  let retryAfterMs: number | undefined;
  if (body && typeof body === 'object') {
    const o = body as Record<string, unknown>;
    const errObj = o.error;
    const ra =
      typeof o.retryAfterMs === 'number'
        ? o.retryAfterMs
        : errObj && typeof errObj === 'object' && typeof (errObj as Record<string, unknown>).retryAfterMs === 'number'
          ? ((errObj as Record<string, unknown>).retryAfterMs as number)
          : undefined;
    if (typeof ra === 'number' && Number.isFinite(ra)) retryAfterMs = ra;
  }

  return {
    code,
    title: base.title,
    message,
    correlationId,
    retryable: base.retryable,
    retryAfterMs,
  };
}

/** Scrub an upstream snapshot/orchestrator message before any reviewer UI. */
export function scrubSnapshotErrorMessage(raw?: string | null): string {
  const normalized = typeof raw === 'string' ? raw.trim() : '';
  if (!normalized || looksLikeSensitiveReviewerMessage(normalized)) {
    return COPY.SERVICE_UNAVAILABLE.message;
  }
  return normalized.length > 240 ? `${normalized.slice(0, 237)}…` : normalized;
}

/** Reviewer-safe message for employer-verification HTTP failures (not dev diagnostics). */
export function reviewerSafeEmployerVerificationError(err: unknown): ReviewerSafeError {
  return toReviewerSafeError({ err, fallbackCode: 'SERVICE_UNAVAILABLE' });
}
